import { ActionsSelectedPlanService } from '../../services/actions-selected-plan.service';
import { AnalyticsService } from '@core/services/analytics.service';
import { DateService } from '@core/services/date.service';
import { DeviceService } from '@core/services/device.service';
import { ReqService } from '@core/services/req.service';
import { SearchStateService } from '@core/services/search-state.service';
import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { IDisneyClusterTickets } from '@core/models/modals/disney-cluster-tickets.interface';
import { Ga4Service } from '@core/services/ga4.service';
import { Store } from '@ngrx/store';
import { AppState } from '@store/app.reducer';
import { StepsEnum } from '../actions-select-plan/store/actions-selected-plan.reducer';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { ISupplement } from '@core/models/Supplement/Supplement.iterface';
import {
  selectRatesByTicket,
  selectRateSelected,
  selectTicketsByDay,
  selectTicketSelected,
  selectTicketsMap,
  selectTicketsState,
} from '@store/disney-tickets/disney-tickets.selectors';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Supplements } from '@core/models/Supplement/Supplements.model';
import {
  checkSelectedRate,
  resetDefaultRate,
  resetSelectedRate,
  setDefaultRate,
  setSelectedRate,
  setSelectedTicket,
  setStartDate,
  unsetSelectedTicket,
} from '@store/disney-tickets/disney-tickets.actions';
import { Rate } from '@core/models/Rate/Rate.model';
import { MealPlan } from '../../../../shared/enums/meal-plan.enum';
import {
  currentStepSelector,
  loadingSelector,
} from '../actions-select-plan/store/actions-seleceted-plan.selector';
import { Charge } from '@core/models/Charge/Charge.model';
import { ChargeType } from '../../../../shared/enums/charge-type.enum';
import { UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MealPlanDisneyComponent } from '../../../detail/disney/meal-plan/meal-plan.component';
import { DisneyTicketComboComponent } from '@commons/disney-ticket-combo/disney-ticket-combo.component';
import { MbIconModule, MbFormFieldModule, MbDatepickerModule, MbCarouselModule } from '@am/moebius';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';

@Component({
    selector: 'disney-selected-plan',
    templateUrl: './disney-selected-plan.component.html',
    styleUrls: ['./disney-selected-plan.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        MbIconModule,
        MbFormFieldModule,
        MbDatepickerModule,
        FormsModule,
        ReactiveFormsModule,
        NgFor,
        MbCarouselModule,
        DisneyTicketComboComponent,
        MealPlanDisneyComponent,
        AsyncPipe,
    ],
})
export class DisneySelectedPlanComponent implements OnInit, OnDestroy {
  @Output() redirectDetail: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('carousel') carousel: ElementRef<any>;
  public ticketsCluster: IDisneyClusterTickets;
  public daySelected: any = 0;
  public dateSelected: Date = new Date();
  public minDate: Date = new Date();
  public maxDate: Date = new Date();
  public tickets: any;
  public dates: any;
  public ticketSelected: ISupplement = null;
  public isMobile = false;
  public isExperts = false;

  public STEP_ENUM = StepsEnum;
  public rateSelected$: Observable<Rate>;
  public unavailableMealPlans: boolean;
  public mealPlanOptional: boolean;
  public daysAvailable$: Observable<number[]>;
  public supplements$: Observable<any[]>;
  public ticketSelected$: Observable<any>;
  public currentStep$: Observable<StepsEnum>;
  public isLoading$: Observable<boolean>;
  public daySelectorFormGroup: UntypedFormGroup = new UntypedFormGroup({
    day: new UntypedFormControl(0),
  });

  private _hasRoomOnlyOption: Subject<boolean> = new BehaviorSubject(true);
  private _ratesWithMealPlan: Subject<Rate[]> = new BehaviorSubject([]);
  private defaultRate: Rate;
  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private searchStateService: SearchStateService,
    private dateService: DateService,
    private analyticsService: AnalyticsService,
    private ga4Service: Ga4Service,
    private store: Store<AppState>,
    private actionsStepsService: ActionsSelectedPlanService,
    private deviceService: DeviceService,
    private reqService: ReqService,
  ) {
    this.isMobile = this.deviceService.isMobile();
    this.isExperts = this.reqService.isExperts();

    this.daysAvailable$ = this.store.select(selectTicketsMap).pipe(
      takeUntil(this.destroy$),
      map((ticketsMap:Map<number, Supplements[]>) =>
        Array.from(ticketsMap.keys()).sort((a, b) => a - b))
    );

    this.ticketSelected$ = this.store
      .select(selectTicketSelected)
      .pipe(takeUntil(this.destroy$));

    this.currentStep$ = this.store.select(currentStepSelector).pipe(
      takeUntil(this.destroy$),
      tap((step) => {
        if (step === StepsEnum.MEAL_PLAN) {
          this.store.dispatch(setDefaultRate({ rate: { ...this.defaultRate } }));
        }
        if (step === StepsEnum.TICKET) {
          this.store.dispatch(resetDefaultRate());
        }
      }),
    );

    this.isLoading$ = this.store.select(loadingSelector);

    this.daySelectorFormGroup.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ day }) => {
        this.setTicketDays(day);
      });

    this.rateSelected$ = this.store.select(selectRateSelected);
  }

  ngOnInit() {
    this.dates = this.searchStateService.getDecodedDate();
    this.minDate = this.createDate(this.dates.from);
    this.maxDate = this.createDate(this.dates.to);
    this.dateSelected = this.minDate;

    this.actionsStepsService.nextStep(StepsEnum.SELECTED_DAY);

    this.store
      .select(selectTicketsState)
      .pipe(take(1))
      .subscribe((state) => {
        this.ticketsCluster = state;
        if (this.ticketsCluster === null) {
          this.redirectDetail.emit(true);
        } else {
          const preSelectedRate: Rate = state.preSelectedRate as Rate;
          this.store.dispatch(setSelectedRate({ rate: preSelectedRate }));
          if (preSelectedRate.supplements && preSelectedRate.supplements.length > 1) {
            const defaultTicket: Charge = preSelectedRate.price.charges.find(
              (charge: Charge) => charge.type === ChargeType.TICKET,
            );
            const ticketSelected: Supplements = preSelectedRate.supplements.find(
              (supplement: Supplements) =>
                defaultTicket.description === supplement.description,
            );
            this._hasRoomOnlyOption.next(false);
            this.daySelectorFormGroup.setValue({ day: ticketSelected.days });
            this.selectTicket(true, ticketSelected);
          }
        }
      });
  }

  // THE CONTROL DATES
  private createDate(date: string) {
    const dateArray = date.split('-');
    const year = parseInt(dateArray[0], 10);
    const month = parseInt(dateArray[1], 10);
    const day = parseInt(dateArray[2], 10);
    const newDate = new Date();
    newDate.setDate(day);
    newDate.setFullYear(year);
    newDate.setMonth(month - 1);
    return newDate;
  }

  onDateSelected(date: Date) {
    if (date !== null) {
      this.dateSelected = date;
      this.store.dispatch(setStartDate({ selectedDate: date }));
      const diff = this.difference(this.dateSelected, this.maxDate);
      if (this.tickets && this.tickets.length === 0) {
        this.daySelected = 0;
      }

      const dataGa4 = {
        element: `fecha :: ${this.dateService.parseLiteralDate(this.dateSelected)}`,
        section: this.ticketsCluster.nameHotel,
        action: 'select',
      };
      this.ga4Service.user_interaction(dataGa4);
    }
  }

  difference(date1, date2) {
    const date1utc = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const date2utc = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate());
    const day = 1000 * 60 * 60 * 24;
    return (date2utc - date1utc) / day;
  }

  setTicketDays(dayNumber: string) {
    this.daySelected = dayNumber;

    this.supplements$ = this.store
      .select<any[]>(selectTicketsByDay, { days: +this.daySelected })
      .pipe(take(1));

    const eventParams = {
      nonInteraction: false,
      section: 'Detail',
      action: 'choose-day',
      label: 'click',
      value: {
        daySelected: this.daySelected,
      },
    };
    this.analyticsService.trackEventClickTicket(eventParams);

    const dataGa4 = {
      element: `cantidad de dias :: ${this.daySelected}`,
      section: this.ticketsCluster.nameHotel,
      action: 'select',
    };
    this.ga4Service.user_interaction(dataGa4);
    this.actionsStepsService.nextStep(StepsEnum.TICKET);
  }

  public selectTicket(isSelected: boolean, ticket: Supplements) {
    if (isSelected) {
      this.ticketSelected = ticket;
      this.store.dispatch(setSelectedTicket({ ticket }));
      this.store
        .select(selectRatesByTicket)
        .pipe(take(1))
        .subscribe((rates: Rate[]) => {
          // Filter rates with meal plan and remove duplicates meal plans.
          const ratesWithMealPlan = rates
            .filter((r: Rate) =>{
             if(r.meal_plan.description.includes('Media') && r.meal_plan.code === MealPlan.HALF_BOARD){
              return false;
             }else if(r.meal_plan.code !== MealPlan.ROOM_ONLY){
              return true;
             }
            } )
            .reduce((acc: Rate[], current: Rate) => {
              const isDuplicate = acc.some(
                (rate: Rate) =>
                  rate.meal_plan.description === current.meal_plan.description,
              );
              if (!isDuplicate) {
                acc.push(current);
              }
              return acc;
            }, []);
          this.unavailableMealPlans = ratesWithMealPlan.length === 0;
          this.mealPlanOptional = rates.some(
            (r: Rate) => r.meal_plan.code === MealPlan.ROOM_ONLY,
          );
          let rateSelected: Rate;
          if (!this.unavailableMealPlans && !this.mealPlanOptional) {
            rateSelected = ratesWithMealPlan[0];
          } else {
            rateSelected = rates[0];
          }
          this.store.dispatch(checkSelectedRate({ rate: rateSelected }));
          this.defaultRate = rateSelected;
          console.log(ratesWithMealPlan);
          this._ratesWithMealPlan.next(ratesWithMealPlan);
        });
    } else {
      this.ticketSelected = null;
      this.store.dispatch(unsetSelectedTicket());
    }
  }

  public areTheSameTickets(ticket1: Supplements, ticket2: Supplements): boolean {
    if (ticket1 === null || ticket2 === null) {
      return false;
    }
    return ticket1.ticket === ticket2.ticket && ticket1.days === ticket2.days;
  }

  public areTheSameRates(rate1: Rate, rate2: Rate): boolean {
    if (rate1 !== null && rate2 !== null) {
      return rate1.meal_plan.description === rate2.meal_plan.description;
    }
    return false;
  }

  public disneyMealPlanSelected(isSelected: boolean, rate: Rate): void {
    if (isSelected) {
      this.store.dispatch(setSelectedRate({ rate }));
      this.store.dispatch(checkSelectedRate({ rate }));
    } else {
      this.store.dispatch(resetSelectedRate());
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get ratesWithMealPlan(): Observable<Rate[]> {
    return this._ratesWithMealPlan.asObservable();
  }

  get hasRoomOnlyOption$(): Observable<boolean> {
    return this._hasRoomOnlyOption.asObservable();
  }
}
